import * as React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Typography, makeStyles, Button, Collapse, Grid } from '@material-ui/core';
import { useIifImportSelector, IifImportSelectors } from '../store/modules/iifImport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { DateTime } from 'luxon'
import { classNames } from '../shared';
import ExpenseDistributionViews from './ExpenseDistributionsView';
import AudCurrencyFormatter from '../utilities/currencyFormatter';
import { ApplicationSettingsSelectors, useApplicationSettingsSelector, TaxMode } from '../store/modules/applicationSettings';
import { red, yellow, green } from '@material-ui/core/colors';
import AttachmentViews from './AttachmentsView';

interface IBillViewProps {
    fileIndex: number;
    billIndex: number;
    billPosition: number;
    importSuccess: boolean;
    importError: boolean;
}

const useStyles = makeStyles(theme => ({
    error: {
        background: `${red[100]} !important`
    },
    financialError: {
        background: `${theme.palette.warning.dark} !important`,
        color: `${theme.palette.common.white} !important`
    },
    warning: {
        '& td': {
            background: yellow[100]
        }
    },
    errorRow: {
        '& td': {
            background: red[100]
        }
    },
    successRow: {
        '& td': {
            background: green[100]
        }
    },
    button: {
        textAlign: 'center',
        minWidth: 'auto',
        padding: theme.spacing(1),

        '& svg': {
            transition: 'all 0.25s linear'
        }
    },
    expanded: {
        '& svg': {
            transform: 'rotate(180deg)'
        }
    },
    noPadding: {
        padding: theme.spacing(0),
        paddingRight: `${theme.spacing(0)} !important`
    },
    padding: {
        padding: theme.spacing(2)
    }
}))

const BillView: React.FunctionComponent<IBillViewProps> = ({
    fileIndex,
    billIndex,
    billPosition,
    importError,
    importSuccess
}) => {
    const bill = useIifImportSelector(state => IifImportSelectors.selectBill(state, billIndex));

    const taxMode = useApplicationSettingsSelector(ApplicationSettingsSelectors.selectTaxMode);

    const classes = useStyles();
    const [expanded, setExpanded] = React.useState(false);
    const [show, setShow] = React.useState(false);

    const onExpandClick = React.useCallback(() => {
        if (expanded) {
            setShow(!show);
        }
        else {
            setExpanded(true);
            setShow(true);
        }
    }, [setShow, show, expanded, setExpanded]);

    const onExited = React.useCallback(() => {
        setExpanded(false);
    }, [setExpanded])

    if (!bill) {
        return null;
    }

    return (
        <>
            <TableRow className={classNames({ [classes.warning]: bill.distributionErrors || bill.attachmentErrors || !bill.attachmentsDownloaded || bill.financialYearError }, { [classes.successRow]: importSuccess }, { [classes.errorRow]: importError })}>
                <TableCell>
                    <Button
                        className={classNames(classes.button, { [classes.expanded]: show })}
                        color="inherit"
                        onClick={onExpandClick}
                    >
                        <FontAwesomeIcon icon={faChevronDown} fixedWidth />
                    </Button>
                </TableCell>
                <TableCell>{billPosition}</TableCell>
                <TableCell className={classNames({ [classes.financialError]: bill.financialYearError })}>{bill.transactionDate ? DateTime.fromISO(bill.transactionDate).toFormat('dd/MM/yyyy') : null}</TableCell>
                <TableCell className={classNames({ [classes.error]: bill.accountError })}>{bill.account?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: bill.vendorError })}>{bill.vendor?.value}</TableCell>
                <TableCell>{bill.docNumber}</TableCell>
                <TableCell className={classNames({ [classes.error]: bill.currencyError })}>{bill.currency?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: bill.amountError })} align="right">
                    {
                        taxMode === TaxMode.Excluded ?
                            AudCurrencyFormatter.format(bill.distributionAmount ?? 0) :
                            AudCurrencyFormatter.format(bill.distributionTotal ?? 0)
                    }
                </TableCell>
            </TableRow>
            {
                expanded && (
                    <TableRow>
                        <TableCell className={classes.noPadding} />
                        <TableCell colSpan={7} className={classes.noPadding}>
                            <Collapse in={show} appear={true} onExited={onExited} >
                                <div className={classes.padding}>
                                    {
                                        bill.dueDate && (
                                            <Grid container spacing={2}>
                                                <Grid item xs={3} lg={2}>
                                                    <Typography>
                                                        <strong>
                                                            Due Date
                                                        </strong>
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography>
                                                        {DateTime.fromISO(bill.dueDate).toFormat('dd/MM/yyyy')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        )
                                    }
                                    {
                                        bill.remitToAddress && (
                                            <Grid container spacing={2}>
                                                <Grid item xs={3} lg={2}>
                                                    <Typography>
                                                        <strong>
                                                            Address
                                                </strong>
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    {
                                                        bill.remitToAddress.line1 && (
                                                            <Typography>
                                                                {bill.remitToAddress.line1}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        bill.remitToAddress.line2 && (
                                                            <Typography>
                                                                {bill.remitToAddress.line2}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        bill.remitToAddress.line3 && (
                                                            <Typography>
                                                                {bill.remitToAddress.line3}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        bill.remitToAddress.line4 && (
                                                            <Typography>
                                                                {bill.remitToAddress.line4}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        bill.remitToAddress.line5 && (
                                                            <Typography>
                                                                {bill.remitToAddress.line5}
                                                            </Typography>
                                                        )
                                                    }
                                                </Grid>
                                            </Grid>
                                        )
                                    }
                                    {
                                        bill.shippingAddress && (
                                            <Grid container spacing={2}>
                                                <Grid item xs={3} lg={2}>
                                                    <Typography>
                                                        <strong>
                                                            Shipping Address
                                                        </strong>
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    {
                                                        bill.shippingAddress.line1 && (
                                                            <Typography>
                                                                {bill.shippingAddress.line1}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        bill.shippingAddress.line2 && (
                                                            <Typography>
                                                                {bill.shippingAddress.line2}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        bill.shippingAddress.line3 && (
                                                            <Typography>
                                                                {bill.shippingAddress.line3}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        bill.shippingAddress.line4 && (
                                                            <Typography>
                                                                {bill.shippingAddress.line4}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        bill.shippingAddress.line5 && (
                                                            <Typography>
                                                                {bill.shippingAddress.line5}
                                                            </Typography>
                                                        )
                                                    }
                                                </Grid>
                                            </Grid>
                                        )
                                    }
                                </div>
                                <ExpenseDistributionViews
                                    distributionView={bill.distributionView}
                                    amount={bill.distributionAmount ?? 0}
                                    tax={bill.distributionTax ?? 0}
                                    total={bill.distributionTotal ?? 0}
                                />
                                <AttachmentViews
                                    fileIndex={fileIndex}
                                    type="bills"
                                    typeIndex={billIndex}
                                    attachmentView={bill.attachmentView}
                                />
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )
            }
        </>
    );
}

export default BillView;


// WEBPACK FOOTER //
// ./src/components/BillView.tsx